import { t } from '@/features/i18n/utils';
import { useWorkspace } from '@/features/workspaces/helpers';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useUpdateWorkspace, useDeleteWorkspace } from '@/features/workspaces/mutations';
import {
  Field,
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/ui/components/form/field';
import { Button } from '@/ui/components/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import { useEffect, useState } from 'react';
import { Form } from '@/ui/components/form';
import { LanguageSelect } from '@/features/i18n/components/language-select';
import { Card } from '@/ui/components/card';
import { H3 } from '@/ui/components/heading';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/_layout/settings/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { workspaceId, projectId } = Route.useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const language = workspace.language ?? 'en-US';
  const updateWorkspace = useUpdateWorkspace();
  const deleteWorkspace = useDeleteWorkspace();

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    if (name === '') {
      setName(workspace.name ?? '');
    }
  }, [workspace, auth.user]);

  return (
    <>
      <Form
        onFormSubmit={() =>
          updateWorkspace.mutate({
            name,
            language,
            projectId: workspace.projectId,
          })
        }
        className="max-w-xl w-full flex flex-col gap-4"
      >
        <Card className="space-y-4 p-4">
          <H3 className="-mt-1">
            {t({ en: 'Workspace information', nb: 'Arbeidsområdeinformasjon' })}
          </H3>
          <Field>
            <FieldLabel>{t({ en: 'Name', nb: 'Navn' })}</FieldLabel>
            <FieldControl
              required
              value={name}
              onValueChange={setName}
              placeholder={t({ en: 'Name', nb: 'Navn' })}
              maxLength={100}
            />
            <FieldDescription>
              {t({
                en: 'This is the public display name. It can be anything you choose.',
                nb: 'Dette er offentlig visningsnavn. Det kan være hva som helst du ønsker.',
              })}
            </FieldDescription>
            <FieldError />
          </Field>
        </Card>
        <Card className="space-y-4 p-4">
          <H3 className="-mt-1">{t({ en: 'Analysis settings', nb: 'Analyseinnstillinger' })}</H3>
          <Field>
            <FieldLabel>{t({ en: 'Output language', nb: 'Utdataspråk' })}</FieldLabel>
            <LanguageSelect readOnly value={language} />
            <FieldDescription>
              {t({
                en: 'All analysis outputs use this language. It is set when the workspace is created and cannot be changed after an analysis has been run.',
                nb: 'Alle analyseresultater bruker dette språket. Det settes når arbeidsområdet opprettes og kan ikke endres etter at en analyse er kjørt.',
              })}
            </FieldDescription>
          </Field>
        </Card>
        <Button type="submit" pending={updateWorkspace.isPending} className="ml-auto">
          {t({ en: 'Update preferences', nb: 'Lagre innstillinger' })}
        </Button>
        <Card className="p-4">
          <H3 className="-mt-1">{t({ en: 'Delete workspace', nb: 'Slett arbeidsområde' })}</H3>
          <p className="text-sm text-muted mt-2 mb-4">
            {t({
              en: 'Woah, approach with caution! There is no going back.',
              nb: 'Wow, vær forsiktig! Det er ingen vei tilbake.',
            })}
          </p>
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogTrigger
              render={
                <Button variant="destructive">
                  {t({ en: 'Delete workspace', nb: 'Slett arbeidsområde' })}
                </Button>
              }
            />
            <AlertDialogPopup>
              <AlertDialogTitle>
                {t({ en: 'Delete workspace', nb: 'Slett arbeidsområde' })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t({
                  en: (
                    <>
                      Are you sure you want to delete <strong>{workspace.name}</strong>?
                    </>
                  ),
                  nb: (
                    <>
                      Er du sikker på at du vil slette <strong>{workspace.name}</strong>?
                    </>
                  ),
                })}
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel />
                <Button
                  variant="destructive"
                  onClick={() => {
                    deleteWorkspace.mutate(workspaceId, {
                      onSuccess: () => {
                        navigate({ to: '/project/$projectId', params: { projectId } });
                        setDeleteDialogOpen(false);
                      },
                    });
                  }}
                  pending={deleteWorkspace.isPending}
                >
                  {t({ en: 'Delete', nb: 'Slett' })}
                </Button>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        </Card>
      </Form>
    </>
  );
}
